import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, pool, getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';
import type {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { TareaPlantacionModel } from '../../../../../models/tareas/tarea-plantacion.model';
import { TareasPlantacionEnvironmentDefinition } from '../environment-variables';
import { TareasPlantacionFormDefinition } from './form-control/form-definition';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

@Component({
    selector: 'app-tareas-plantacion-form',
    standalone: false,
    templateUrl: './tareas-plantacion-form.component.html',
    styleUrls: ['./tareas-plantacion-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TareasPlantacionFormComponent extends BaseForm<TareaPlantacionModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de plantación...';

    public env: TareasPlantacionEnvironmentDefinition = new TareasPlantacionEnvironmentDefinition();
    public override model: TareaPlantacionModel = {};
    public form!: TareasPlantacionFormDefinition;
    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.tareasApi.plantacion.GET;
    public reactiveFormRecordId = '';

    public initialPreviousData: TareaPlantacionModel | null = null;

    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public siexApi: SiexApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.plantacion.PUT,
            tareasApi.plantacion.POST,
            'plantacion',
            'Actualizar Tarea Plantación',
            'Realizar Tarea Plantación',
            'Duplicar Tarea Plantación',
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.resetExternalDropdowns();
            this.dashboard.clientChanges$.subscribe(() => {
                this.fincasApi.fincasGET.unsuscribe();
            });

            this.softInit(this.getType());
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toPlantacionModel(this.getInitialModel()));

            this.form = new TareasPlantacionFormDefinition(this.getType(), initialModel);
            this.form.model = this.toPlantacionModel(initialModel);
            this.syncModelFromForm();
            this.initialPreviousData = this.toPlantacionModel(initialModel);
            this.configureDeclarativeCatalogDataSources();

            await this.initFormData();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de plantación.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TareaPlantacionModel>): void {
        const changedFieldName = String(event.changedField ?? '');
        const stableValue = this.captureStableValueChange(event.value as TareaPlantacionModel);

        this.form.model = this.toPlantacionModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<TareaPlantacionModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaPlantacionModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: lastChangedFieldName,
            changedFields: [String(lastChangedFieldName ?? '')],
            origin: 'user',
        });
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareaPlantacionModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as TareaPlantacionModel);

        this.form.model = this.toPlantacionModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.form.updateMantenimientoLabel();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<TareaPlantacionModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaPlantacionModel>,
    ): void {
        const changedFieldName = String(lastChangedFieldName);

        this.form.model = this.toPlantacionModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHoras();
        }

        if (changedFieldName === 'mantenimiento') {
            this.form.updateMantenimientoLabel();
            this.refreshReactiveDropdownMappings();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }

        if (changedFieldName === 'id_cultivo') {
            this.clearPlantacionCascade(['id_variedad']);
        }
    }

    public onAfterSend(): void {
        StorageManager.saveSessionDataDonJardin(
            this.form.model.id_finca ?? '',
            this.form.model.hora_inicio ?? '',
            this.form.model.hora_fin?.toString() ?? '',
        );
    }

    private getInitialModel(): Partial<TareaPlantacionModel> {
        return {
            ...(this.getModel() ?? {}),
        };
    }

    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        const buildsRequest = this.getDropdownBuildsForConfiguredCatalogs();

        return pool(buildsRequest, () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.applyCreationDefaults();
                this.loadingData = false;
                this.finalizeExternalDropdowns();
                this.refreshReactiveDropdownMappings();
                this.cdr.markForCheck();
            })
            .catch((error: unknown) => {
                this.loadingData = false;
                this.finalizeExternalDropdowns();
                throw error;
            });
    }

    private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
        const configuredCatalogs = this.getConfiguredDropdownCatalogs();
        const buildsRequest: ReturnType<typeof build>[] = [];

        if (configuredCatalogs.has('id_finca')) {
            buildsRequest.push(build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true));
        }

        if (configuredCatalogs.has('id_cliente')) {
            buildsRequest.push(build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }));
        }

        return buildsRequest;
    }

    private getConfiguredDropdownCatalogs(): Set<string> {
        const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

        return new Set([
            ...(dropdownCatalogs?.critical ?? []),
            ...(dropdownCatalogs?.deferred ?? []),
            ...(dropdownCatalogs?.lazy ?? []),
        ]);
    }

    private applyCreationDefaults(): void {
        const defaultFincaId = this.form.lists.fincas.values[0]?.value?.id ?? null;

        if (this.form.model.id_finca || !defaultFincaId) {
            return;
        }

        this.form.model = this.toPlantacionModel({
            ...this.form.model,
            id_finca: defaultFincaId,
        });
        this.syncModelFromForm();
    }

    private updateHoras(): void {
        const horas = this.form.calculateHoras();

        if (this.form.model.horas === horas) {
            return;
        }

        this.form.model = this.toPlantacionModel({
            ...this.form.model,
            horas,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm({ horas }, true);
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        this.clearPlantacionCascade(['id_finca']);

        if (!selectedClientId || this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        this.reloadClientDropdowns(selectedClientId);
    }

    private clearPlantacionCascade(fieldNames: string[]): void {
        const updates: Record<string, unknown> = {};

        for (const fieldName of fieldNames) {
            updates[fieldName] = null;
        }

        this.form.model = this.toPlantacionModel({
            ...this.form.model,
            ...updates,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm(updates as Partial<TareaPlantacionModel>, false);
        this.refreshReactiveDropdownMappings();
    }

    private reloadClientDropdowns(selectedClientId: string): void {
        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;
        this.resetExternalDropdowns();
        this.refreshReactiveDropdownMappings();

        const selectedClient = getDropdownSelectedSingle(this.form.lists.clientes);

        if (selectedClient) {
            StorageManager.saveClient(selectedClient);
        }

        this.dashboard.emitClientChange();

        this.formBuilderRef?.refreshDeclarativeDropdown('id_finca');
        this.finalizeExternalDropdowns();
        this.isReloadingClientDropdowns = false;
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de plantación: ' + error.message;
        }

        return 'No se ha podido cargar el formulario de plantación. Revisa la conexión e inténtalo de nuevo.';
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private configureDeclarativeCatalogDataSources(): void {
        this.form.setCatalogDataSources({
            fincas: this.fincasApi.fincasGET,
            clientes: this.clientesApi.clientesGET,
            trabajadores: this.trabajadoresApi.trabajadoresGET,
            cultivos: this.siexApi.cultivosGET,
            variedades: this.siexApi.variedadesGET,
        });
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toPlantacionModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toPlantacionModel(
        sourceModel: ReactiveFormValue<TareaPlantacionModel> | Partial<TareaPlantacionModel> | Record<string, unknown>,
    ): TareaPlantacionModel {
        const normalizedModel: Record<string, TareaPlantacionModel[keyof TareaPlantacionModel]> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value as TareaPlantacionModel[keyof TareaPlantacionModel];
            }
        }

        return normalizedModel as TareaPlantacionModel;
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
